import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import type { AccountKindValue } from '@internal/datasphere-personas';
import {
  UserOption,
  UserOptionValue as User,
  isWrongAccountKind,
} from './UserOption';

export type UserSearchOption = User;

type Margin = 'none' | 'dense' | 'normal';

interface UserEmailSearchFieldProps {
  value?: string;
  onChange: (value?: string) => void;
  /** Receives the full selected user (including display name). */
  onUserChange?: (user?: UserSearchOption) => void;
  label: string;
  required?: boolean;
  rawErrors?: string[];
  helperText?: string;
  margin?: Margin;
  disabled?: boolean;
  /** The account kind this field needs; the other kind cannot be picked. */
  accountKind?: AccountKindValue;
}

export const UserEmailSearchField = ({
  value,
  onChange,
  onUserChange,
  label,
  required,
  rawErrors,
  helperText,
  margin = 'normal',
  disabled,
  accountKind,
}: UserEmailSearchFieldProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selectedUser, setSelectedUser] = useState<User | null>(
    value ? { email: value, displayName: value } : null,
  );
  const [inputValue, setInputValue] = useState(value ?? '');
  const [options, setOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value ?? '');
    setSelectedUser(value ? { email: value, displayName: value } : null);
  }, [value]);

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setOptions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/users/search?q=${encodeURIComponent(trimmed)}`,
        );

        if (response.ok) {
          const users: User[] = await response.json();
          setOptions(users);
        } else {
          setOptions([]);
        }
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, discoveryApi, fetchApi]);

  const hasError = !!rawErrors?.length;

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={options}
      loading={loading}
      value={selectedUser}
      inputValue={inputValue}
      getOptionLabel={option => option.email}
      getOptionSelected={(option, selected) => option.email === selected.email}
      getOptionDisabled={option => isWrongAccountKind(option, accountKind)}
      filterOptions={x => x}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Type 2+ characters to search'
          : 'No users found'
      }
      onChange={(_, newValue: User | null) => {
        setSelectedUser(newValue);
        onChange(newValue?.email ?? undefined);
        onUserChange?.(newValue ?? undefined);
      }}
      onInputChange={(_, newInput, reason) => {
        setInputValue(newInput);
        if (reason === 'clear') {
          setSelectedUser(null);
          onChange(undefined);
          onUserChange?.(undefined);
        }
      }}
      renderOption={option => (
        <UserOption user={option} accountKind={accountKind} />
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={hasError}
          helperText={hasError ? rawErrors?.join(', ') : helperText}
          margin={margin}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
